import React, { forwardRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ToggleOn,
  ToggleOff,
  InfoOutlined as InfoOutlinedIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getTableCellStyles,
  getTableContainerStyles,
  getTableHeaderStyles,
  getTableRowStyles,
} from '../../styles/tableStyles';
import {
  COLUMN_TOOLTIP_AUTO_DRAFT,
  COLUMN_TOOLTIP_AUTO_REPLY,
  COLUMN_TOOLTIP_SENSITIVITY,
  LABEL_SETTINGS_INFO_BANNER,
  OPEN_SENSITIVITY_WARNING,
  SENSITIVITY_OPTIONS,
  getAutoDraftRowTooltip,
  getAutoReplyRowTooltip,
  type LabelAutomationSetting,
  type NoSubtagsEmptyStateCopy,
  type SensitivityValue,
} from './automationTabConstants';
import AutomationNoSubtagsEmptyState from './AutomationNoSubtagsEmptyState';

const EM_DASH = '—';

const pulseKeyframes = {
  '@keyframes automationRowPulse': {
    '0%': { backgroundColor: 'transparent' },
    '25%': { backgroundColor: 'rgba(25, 118, 210, 0.18)' },
    '50%': { backgroundColor: 'rgba(25, 118, 210, 0.28)' },
    '75%': { backgroundColor: 'rgba(25, 118, 210, 0.18)' },
    '100%': { backgroundColor: 'transparent' },
  },
};

const VerbatimTooltip: React.FC<{
  text: string;
  ariaLabel?: string;
  children: React.ReactElement;
}> = ({ text, ariaLabel = 'Help', children }) => (
  <Tooltip
    title={
      <Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>
        {text}
      </Box>
    }
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 400 } } }}
  >
    {children}
  </Tooltip>
);

const HeaderHelp: React.FC<{ text: string; ariaLabel: string }> = ({ text, ariaLabel }) => (
  <VerbatimTooltip text={text} ariaLabel={ariaLabel}>
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary', ml: 0.25 }} aria-label={ariaLabel}>
      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </VerbatimTooltip>
);

const ToggleControl: React.FC<{
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
  tooltipText: string;
  ariaLabel: string;
}> = ({ enabled, onToggle, disabled = false, tooltipText, ariaLabel }) => {
  const muiTheme = useMuiTheme();
  return (
    <VerbatimTooltip text={tooltipText} ariaLabel={ariaLabel}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <IconButton
          onClick={onToggle}
          disabled={disabled}
          sx={{ p: 0, '&:hover': { backgroundColor: 'transparent' } }}
          aria-label={ariaLabel}
        >
          {enabled ? (
            <ToggleOn sx={{ fontSize: 40, color: muiTheme.palette.primary.main }} />
          ) : (
            <ToggleOff sx={{ fontSize: 40, color: muiTheme.palette.action.disabled }} />
          )}
        </IconButton>
        <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.secondary'} sx={{ minWidth: 28 }}>
          {enabled ? 'ON' : 'OFF'}
        </Typography>
      </Box>
    </VerbatimTooltip>
  );
};

export interface AutomationLabelSettingsSectionProps {
  labels: LabelAutomationSetting[];
  onChange: (labels: LabelAutomationSetting[]) => void;
  masterEnabled: boolean;
  disabled?: boolean;
  hasSubtags: boolean;
  onOpenChannelConfigEdit?: () => void;
  noSubtagsEmptyStateCopy?: NoSubtagsEmptyStateCopy;
  pulseFirstRow?: boolean;
}

const AutomationLabelSettingsSection = forwardRef<HTMLDivElement, AutomationLabelSettingsSectionProps>(
  (
    {
      labels,
      onChange,
      masterEnabled,
      disabled = false,
      hasSubtags,
      onOpenChannelConfigEdit,
      noSubtagsEmptyStateCopy,
      pulseFirstRow = false,
    },
    ref
  ) => {
    const muiTheme = useMuiTheme();
    const { isDarkMode } = useTheme();
    const rowDisabled = disabled || !masterEnabled;
    const showOpenSensitivityWarning = labels.some(
      (row) =>
        row.auto_draft_enabled && row.auto_reply_enabled && row.auto_reply_sensitivity === 'open'
    );

    const updateRow = (index: number, patch: Partial<LabelAutomationSetting>) => {
      onChange(labels.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const handleAutoDraftToggle = (index: number) => {
      const row = labels[index];
      const nextDraft = !row.auto_draft_enabled;
      if (!nextDraft) {
        updateRow(index, { ...row, auto_draft_enabled: false });
        return;
      }
      updateRow(index, {
        ...row,
        auto_draft_enabled: true,
        auto_reply_enabled: row.auto_reply_enabled ?? false,
      });
    };

    const handleAutoReplyToggle = (index: number) => {
      const row = labels[index];
      if (!row.auto_draft_enabled) return;
      const nextReply = !row.auto_reply_enabled;
      if (!nextReply) {
        updateRow(index, { ...row, auto_reply_enabled: false });
        return;
      }
      updateRow(index, {
        ...row,
        auto_reply_enabled: true,
        auto_reply_sensitivity: row.auto_reply_sensitivity ?? 'balanced',
      });
    };

    const handleSensitivityChange = (index: number, value: SensitivityValue) => {
      updateRow(index, { auto_reply_sensitivity: value });
    };

    const cardSx = {
      px: 3,
      py: 3,
      backgroundColor: muiTheme.palette.background.paper,
      borderRadius: 2,
      border: `1px solid ${muiTheme.palette.divider}`,
      boxShadow: muiTheme.shadows[1],
    };

    return (
      <Box ref={ref} sx={cardSx}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Label Settings
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: 2,
            p: 1.5,
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 1,
            backgroundColor: isDarkMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
            opacity: masterEnabled ? 1 : 0.45,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main', mt: 0.25, flexShrink: 0 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flex: 1, minWidth: 0, width: '100%', lineHeight: 1.5 }}
          >
            {LABEL_SETTINGS_INFO_BANNER}
          </Typography>
        </Box>

        {showOpenSensitivityWarning && (
          <Alert
            severity="warning"
            sx={{ mb: 2, opacity: masterEnabled ? 1 : 0.45 }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
              {OPEN_SENSITIVITY_WARNING}
            </Typography>
          </Alert>
        )}

        {!hasSubtags ? (
          <AutomationNoSubtagsEmptyState
            onOpenChannelConfigEdit={onOpenChannelConfigEdit}
            copy={noSubtagsEmptyStateCopy}
            dimmed={!masterEnabled}
          />
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ ...getTableContainerStyles(), opacity: masterEnabled ? 1 : 0.45, ...pulseKeyframes }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={getTableHeaderStyles(isDarkMode, true)}>Label</TableCell>
                  <TableCell sx={getTableHeaderStyles(isDarkMode, true)} align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      Auto-Draft
                      <HeaderHelp text={COLUMN_TOOLTIP_AUTO_DRAFT} ariaLabel="Auto-Draft column help" />
                    </Box>
                  </TableCell>
                  <TableCell sx={getTableHeaderStyles(isDarkMode, true)} align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      Auto-Reply
                      <HeaderHelp text={COLUMN_TOOLTIP_AUTO_REPLY} ariaLabel="Auto-Reply column help" />
                    </Box>
                  </TableCell>
                  <TableCell sx={getTableHeaderStyles(isDarkMode, true)} align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      Sensitivity
                      <HeaderHelp text={COLUMN_TOOLTIP_SENSITIVITY} ariaLabel="Sensitivity column help" />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labels.map((row, index) => {
                  const draftOn = row.auto_draft_enabled;
                  const replyOn = draftOn && row.auto_reply_enabled === true;
                  const isFirstRow = index === 0;
                  const sensitivity = row.auto_reply_sensitivity ?? 'balanced';

                  return (
                    <TableRow
                      key={row.name}
                      sx={{
                        ...getTableRowStyles(),
                        ...(isFirstRow && pulseFirstRow
                          ? {
                              animation: 'automationRowPulse 1.2s ease-in-out 2',
                            }
                          : {}),
                      }}
                    >
                        <TableCell sx={getTableCellStyles()}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            #{row.name}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={getTableCellStyles()}>
                          <ToggleControl
                            enabled={draftOn}
                            onToggle={() => handleAutoDraftToggle(index)}
                            disabled={rowDisabled}
                            tooltipText={getAutoDraftRowTooltip(row.name, draftOn)}
                            ariaLabel={`Auto-Draft for ${row.name}`}
                          />
                        </TableCell>

                        <TableCell align="center" sx={getTableCellStyles()}>
                          {!draftOn ? (
                            <VerbatimTooltip
                              text={getAutoReplyRowTooltip(row.name, false, false)}
                              ariaLabel={`Auto-Reply unavailable for ${row.name}`}
                            >
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                sx={{ cursor: 'default' }}
                              >
                                {EM_DASH}
                              </Typography>
                            </VerbatimTooltip>
                          ) : (
                            <ToggleControl
                              enabled={replyOn}
                              onToggle={() => handleAutoReplyToggle(index)}
                              disabled={rowDisabled}
                              tooltipText={getAutoReplyRowTooltip(
                                row.name,
                                true,
                                replyOn,
                                row.auto_reply_sensitivity
                              )}
                              ariaLabel={`Auto-Reply for ${row.name}`}
                            />
                          )}
                        </TableCell>

                        <TableCell align="center" sx={getTableCellStyles()}>
                          {!draftOn || !replyOn ? (
                            <Typography variant="body2" color="text.secondary">
                              {EM_DASH}
                            </Typography>
                          ) : (
                            <FormControl size="small" sx={{ minWidth: 110 }} disabled={rowDisabled}>
                              <Select
                                value={sensitivity}
                                onChange={(e) =>
                                  handleSensitivityChange(index, e.target.value as SensitivityValue)
                                }
                                inputProps={{ 'aria-label': `Sensitivity for ${row.name}` }}
                                renderValue={(value) => {
                                  const option = SENSITIVITY_OPTIONS.find((opt) => opt.value === value);
                                  if (value === 'open') {
                                    return (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                        <span>{option?.label ?? 'Open'}</span>
                                      </Box>
                                    );
                                  }
                                  return option?.label ?? value;
                                }}
                                sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                              >
                                {SENSITIVITY_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.value === 'open' ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                        <span>{opt.label}</span>
                                      </Box>
                                    ) : (
                                      opt.label
                                    )}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                      </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  }
);

AutomationLabelSettingsSection.displayName = 'AutomationLabelSettingsSection';

export default AutomationLabelSettingsSection;
